import React, { useState, useEffect } from "react";
import { useUserProfile } from "../fetches/GetUserProfileFetch";
import { useNavigate } from "react-router-dom";
import LogoutModal from "./Modals/LogoutModal";
import "../css/Fonts.css";

const UserProfileSection = () => {
  const { getUserProfile } = useUserProfile();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const navigate = useNavigate();

  // Define mapping from roleType to path
  const roleToPathMapping = {
    1: "/summary",
    2: "/hr",
    3: "/analytics",
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = () => {
    localStorage.removeItem("accessToken");
    setShowLogoutModal(false);
    navigate("/login");
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  const handleNotifications = () => {
    console.log("Notifications clicked");
  };

  const handleSettings = () => {
    console.log("Settings clicked");
    // Potentially navigate to a settings page: navigate('/settings');
  };

  const handleProfileAreaClick = () => {
    if (profile && profile.roleType) {
      const path = roleToPathMapping[profile.roleType];
      if (path) {
        navigate(path);
      } else {
        console.warn(`No path defined for roleType: ${profile.roleType}`);
        // Optionally navigate to a default page if role path is not found
        // navigate('/');
      }
    }
  };

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const data = await getUserProfile();
        if (isMounted) {
          setProfile(data);
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [getUserProfile]); // Added getUserProfile to dependency array as it's used in effect

  const roleMapping = {
    1: "Admin",
    2: "Hr",
    3: "Analyst",
  };

  if (loading) {
    return <div>Loading...</div>; // Or a skeleton loader
  }

  if (!profile) {
    return <div>Error loading profile.</div>;
  }

  const roleText = profile.roleType ? roleMapping[profile.roleType] : "";
  const nameParts = profile.fullName ? profile.fullName.split(" ") : [];
  const firstName = nameParts[0] || "";
  const lastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";

  return (
    <>
      <div className="flex items-center">
        {/* Make this div clickable */}
        <div
          className="gap-3 items-center flex px-3 cursor-pointer"
          onClick={handleProfileAreaClick}
          role="button" // For accessibility
          tabIndex={0} // For keyboard accessibility
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") handleProfileAreaClick();
          }} // For keyboard accessibility
          title={`Go to ${roleText || "dashboard"}`} // Tooltip for the action
        >
          {/* Name and Role Section */}
          <div className="flex flex-col items-end">
            <div className="nickname-label">
              {firstName} {lastName}
            </div>
            {roleText && (
              <div className="flex items-center nickname-sub-label rounded-3xl w-fit bg-[#D52037]">
                <span className="px-2">{roleText}</span>
              </div>
            )}
          </div>

          {/* Empty SVG - consider removing if not used, or give it a purpose */}
          <svg
            viewBox={`0 0 ${Math.max(32, roleText.length * 7 + 16)} 17`}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid meet"
            aria-hidden="true" // If purely decorative
          ></svg>
          {/* Avatar */}
          <img
            src={profile.avatarUrl || "/assets/images/avatar.png"}
            alt="User Avatar"
            className="w-[50px] h-[50px] rounded-full"
          />
        </div>
        {/* Buttons Section */}
        <div className="flex items-center gap-1">
          <button
            className="focus:outline-none transition-transform hover:scale-110 p-1.5 rounded-full hover:bg-gray-100" // Added padding and hover bg for better click area
            onClick={handleNotifications}
            title="Notifications"
            aria-label="Notifications"
          >
            <svg
              width="35"
              height="36"
              viewBox="0 0 35 36"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect y="0.5" width="35" height="35" rx="17.5" fill="#F4F7FE" />
              <path
                d="M24.7905 23.29L23.5005 22V17C23.5005 13.93 21.8605 11.36 19.0005 10.68V10C19.0005 9.17 18.3305 8.5 17.5005 8.5C16.6705 8.5 16.0005 9.17 16.0005 10V10.68C13.1305 11.36 11.5005 13.92 11.5005 17V22L10.2105 23.29C9.58054 23.92 10.0205 25 10.9105 25H24.0805C24.9805 25 25.4205 23.92 24.7905 23.29ZM21.5005 23H13.5005V17C13.5005 14.52 15.0105 12.5 17.5005 12.5C19.9905 12.5 21.5005 14.52 21.5005 17V23ZM17.5005 28C18.6005 28 19.5005 27.1 19.5005 26H15.5005C15.5005 27.1 16.3905 28 17.5005 28Z"
                fill="#A3AED0"
              />
            </svg>
          </button>

          <button
            className="focus:outline-none transition-transform hover:scale-110 p-1.5 rounded-full hover:bg-gray-100" // Added padding and hover bg
            onClick={handleSettings}
            title="Settings"
            aria-label="Settings"
          >
            <svg
              width="35"
              height="36"
              viewBox="0 0 35 36"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect y="0.5" width="35" height="35" rx="17.5" fill="#F4F7FE" />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10.6268 13.2743C10.4325 12.1257 11.5109 11.1652 12.6507 11.4716L13.5136 11.7036C14.1985 11.8877 14.9252 11.6108 15.3063 11.0204L16.1335 9.73937C16.7699 8.75354 18.2301 8.75354 18.8665 9.73937L19.6936 11.0204C20.0747 11.6108 20.8014 11.8877 21.4863 11.7036L22.3493 11.4716C23.4891 11.1652 24.5675 12.1257 24.3732 13.2743L24.1604 14.5331C24.051 15.18 24.3542 15.8271 24.9248 16.1645L25.7136 16.631C26.7621 17.2511 26.7621 18.7489 25.7136 19.369L24.9248 19.8355C24.3542 20.1729 24.051 20.82 24.1604 21.467L24.3732 22.7258C24.5675 23.8743 23.4891 24.8348 22.3493 24.5285L21.4863 24.2965C20.8014 24.1123 20.0747 24.3893 19.6936 24.9796L18.8665 26.2606C18.2301 27.2465 16.7699 27.2465 16.1335 26.2606L15.3063 24.9796C14.9252 24.3893 14.1985 24.1123 13.5136 24.2965L12.6507 24.5285C11.5109 24.8348 10.4325 23.8743 10.6268 22.7258L10.8396 21.467C10.949 20.82 10.6459 20.1729 10.0753 19.8355L9.2864 19.369C8.23786 18.7489 8.23787 17.2511 9.2864 16.631L10.0753 16.1645C10.6459 15.8271 10.949 15.18 10.8396 14.5331L10.6268 13.2743ZM15.0703 13.2105H12.6407V15.6053L10.211 18L12.6407 20.3947V22.7895H15.0703L17.5 25.1842L19.9297 22.7895H22.3593V20.3947L24.7889 18L22.3593 15.6053V13.2105H19.9297L17.5 10.8158L15.0703 13.2105Z"
                fill="#A3AED0"
              />
              <path
                d="M21.1421 18C21.1421 19.9839 19.5104 21.5921 17.4976 21.5921C15.4849 21.5921 13.8532 19.9839 13.8532 18C13.8532 16.0161 15.4849 14.4079 17.4976 14.4079C19.5104 14.4079 21.1421 16.0161 21.1421 18ZM15.4706 18C15.4706 19.1034 16.3781 19.9979 17.4976 19.9979C18.6171 19.9979 19.5247 19.1034 19.5247 18C19.5247 16.8966 18.6171 16.0021 17.4976 16.0021C16.3781 16.0021 15.4706 16.8966 15.4706 18Z"
                fill="#A3AED0"
              />
            </svg>
          </button>

          <button
            className="focus:outline-none transition-transform hover:scale-110 p-1.5 rounded-full hover:bg-gray-100" // Added padding and hover bg
            onClick={handleLogoutClick}
            title="Logout"
            aria-label="Logout"
          >
            <svg
              width="35"
              height="36"
              viewBox="0 0 35 36"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect y="0.5" width="35" height="35" rx="17.5" fill="#F4F7FE" />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10.5 16.1081V19.8919C10.5 21.6756 10.5 22.5674 11.0943 23.1216C11.6342 23.625 12.473 23.671 14.0564 23.6753C13.9727 23.1508 13.9552 22.5242 13.9508 21.7869C13.949 21.4735 14.22 21.218 14.5561 21.2162C14.8923 21.2145 15.1663 21.4672 15.1682 21.7806C15.1731 22.6081 15.1962 23.1947 15.2839 23.6398C15.3684 24.0688 15.504 24.317 15.7016 24.5013C15.9262 24.7107 16.2416 24.8473 16.8371 24.9219C17.4502 24.9988 18.2627 25 19.4276 25H20.2392C21.4041 25 22.2166 24.9988 22.8296 24.9219C23.4252 24.8473 23.7405 24.7107 23.9651 24.5013C24.1898 24.2918 24.3362 23.9978 24.4162 23.4425C24.4987 22.8709 24.5 22.1133 24.5 21.027V14.973C24.5 13.8867 24.4987 13.1291 24.4162 12.5575C24.3362 12.0022 24.1898 11.7082 23.9651 11.4987C23.7405 11.2893 23.4252 11.1527 22.8296 11.0781C22.2166 11.0012 21.4041 11 20.2392 11H19.4276C18.2627 11 17.4502 11.0012 16.8371 11.0781C16.2416 11.1527 15.9262 11.2893 15.7016 11.4987C15.504 11.6829 15.3684 11.9312 15.2839 12.3602C15.1962 12.8053 15.1731 13.3919 15.1682 14.2194C15.1663 14.5328 14.8923 14.7855 14.5561 14.7838C14.22 14.782 13.949 14.5265 13.9508 14.2131C13.9552 13.4758 13.9727 12.8492 14.0564 12.3247C12.473 12.329 11.6342 12.375 11.0943 12.8785C10.5 13.4326 10.5 14.3244 10.5 16.1081ZM12.5043 18.4013C12.2666 18.1797 12.2666 17.8203 12.5043 17.5987L14.1275 16.0852C14.3652 15.8635 14.7506 15.8635 14.9883 16.0852C15.226 16.3068 15.226 16.6662 14.9883 16.8879L14.4043 17.4325H20.239C20.5751 17.4325 20.8477 17.6866 20.8477 18C20.8477 18.3134 20.5751 18.5675 20.239 18.5675H14.4043L14.9883 19.1121C15.226 19.3338 15.226 19.6932 14.9883 19.9148C14.7506 20.1365 14.3652 20.1365 14.1275 19.9148L12.5043 18.4013Z"
                fill="#A3AED0"
              />
            </svg>
          </button>
        </div>

        <LogoutModal
          isOpen={showLogoutModal}
          onClose={handleLogoutCancel}
          onConfirm={handleLogoutConfirm}
        />
      </div>
    </>
  );
};

export default UserProfileSection;
